import { forwardRef } from "react";
import { motion } from "framer-motion";
import { buttonTap } from "@styles/motion";

/**
 * IconButton — circular, icon-only tap target. Used for back buttons,
 * favorite toggles, filter triggers, and top-bar actions.
 *
 * @param {"filled"|"ghost"|"outline"} variant
 * @param {"sm"|"md"|"lg"} size
 * @param {boolean} active — visually marks a toggled-on state (e.g. saved favorite)
 */
const VARIANT_CLASSES = {
  filled: "bg-white shadow-card text-brand-ink hover:bg-brand-cream",
  ghost: "bg-transparent text-brand-ink hover:bg-brand-line/40",
  outline: "bg-transparent border border-brand-line text-brand-ink hover:border-brand-primary",
};

const SIZE_CLASSES = {
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-12 h-12",
};

const IconButton = forwardRef(function IconButton(
  {
    children,
    variant = "filled",
    size = "md",
    active = false,
    disabled = false,
    "aria-label": ariaLabel,
    className = "",
    ...rest
  },
  ref
) {
  return (
    <motion.button
      ref={ref}
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      whileTap={disabled ? undefined : buttonTap.whileTap}
      className={[
        "inline-flex items-center justify-center rounded-pill transition-colors duration-150",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        active ? "text-brand-gold" : "",
        disabled ? "opacity-40 cursor-not-allowed" : "",
        className,
      ].join(" ")}
      {...rest}
    >
      {children}
    </motion.button>
  );
});

export default IconButton;
